import React from "react";
import "./Button.css";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  palette: "primary" | "secondary" | "accent";
  size: "small" | "medium" | "large";
  colorScheme: "light" | "medium" | "dark";
  hasIcon: boolean;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  palette,
  size,
  className = "",
  colorScheme,
  style,
  children,
  hasIcon = false,
  disabled = false,
  ...rest
}) => {
  const buttonClassName = `btn ${palette} ${size} ${hasIcon ? "icon" : ""} ${palette}-${colorScheme} ${className}`.trim();

  return (
    <button className={`${buttonClassName} ${disabled ? 'cursor-disabled' : ''}`} style={style} disabled={disabled} {...rest}>
      {children}
    </button>
  );
};

/**
 * A styled, flexible button component with support for palette, size, and color scheme variations.
 * Extends native HTML button attributes and provides consistent styling across the application.
 *
 * @component
 * @example
 * // Basic usage
 * <Button palette="primary" size="medium" colorScheme="light" hasIcon={false}>
 *   Click me
 * </Button>
 *
 * @example
 * // With icon and custom styling
 * <Button
 *   palette="secondary"
 *   size="large"
 *   colorScheme="dark"
 *   hasIcon={true}
 *   className="custom-button"
 *   onClick={() => console.log('Clicked')}
 * >
 *   <Icon /> Save Changes
 * </Button>
 *
 * @param {ButtonProps} props - Props passed to the Button component
 * @param {"primary" | "secondary" | "accent"} props.palette - Defines the visual style theme of the button
 * @param {"small" | "medium" | "large"} props.size - Sets the width and visual scale of the button
 * @param {"light" | "medium" | "dark"} props.colorScheme - Adjusts the tone within the selected palette
 * @param {boolean} props.hasIcon - Indicates whether the button includes an icon (affects spacing and styling)
 * @param {React.ReactNode} props.children - Button content (text, icon, etc.)
 * @param {string} [props.className] - Additional custom CSS class names
 * @param {React.CSSProperties} [props.style] - Inline styles for the button
 * @returns {JSX.Element} The rendered button element
 */
